import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RotateCw } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api/actualizacion-familiar/admin/documentacion/envio';
const ESTADOS = { en_curso: 'Enviando', completado: 'Completado', error: 'Detenido por error' };

const Cifra = ({ label, value, testid, color = 'text-gray-900' }) => (
  <div><p className={`text-xl font-bold ${color}`} data-testid={testid}>{value}</p><p className="text-xs text-gray-500">{label}</p></div>
);

export const EnvioPedidosEstado = ({ version, onCambio }) => {
  const [job, setJob] = useState(null);
  const [reanudando, setReanudando] = useState(false);

  const cargar = useCallback(async () => {
    const j = (await axios.get(API)).data.job;
    setJob((prev) => {
      if (prev?.estado === 'en_curso' && j?.estado !== 'en_curso') onCambio?.();
      return j;
    });
  }, [onCambio]);

  useEffect(() => { cargar().catch(() => {}); }, [cargar, version]);
  useEffect(() => {
    if (job?.estado !== 'en_curso') return undefined;
    const t = setInterval(() => cargar().catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [job?.estado, cargar]);

  if (!job) return null;
  const trabado = job.estado === 'error' || (job.estado === 'en_curso' && (!job.lock_until || new Date(job.lock_until) < new Date()));
  const reanudar = async () => {
    setReanudando(true);
    try { await axios.post(`${API}/${job.id}/reanudar`); toast.success('Envío reanudado'); await cargar(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo reanudar'); } finally { setReanudando(false); }
  };

  return (
    <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="af-envio-estado">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-gray-900 flex items-center gap-2">
          {job.estado === 'en_curso' && !trabado && <Loader2 className="h-4 w-4 animate-spin text-violet-600" />}
          Último pedido de documentación · <span data-testid="af-envio-estado-label">{trabado && job.estado === 'en_curso' ? 'Interrumpido' : ESTADOS[job.estado]}</span>
        </p>
        <span className="text-xs text-gray-500">{new Date(job.iniciado).toLocaleString('es-AR')} · {job.iniciado_por}</span>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <Cifra label="Total" value={job.total} testid="af-envio-total" />
        <Cifra label="Enviados" value={job.enviados} testid="af-envio-enviados" color="text-green-700" />
        <Cifra label="Fallidos" value={job.fallidos} testid="af-envio-fallidos" color="text-red-600" />
        <Cifra label="Pendientes" value={job.pendientes} testid="af-envio-pendientes" color="text-amber-600" />
      </div>
      {job.ultimo_error && <p className="text-xs text-red-600" data-testid="af-envio-error">{job.ultimo_error}</p>}
      {job.fallidos_detalle?.length > 0 && (
        <details className="text-xs text-gray-600" data-testid="af-envio-fallidos-detalle">
          <summary className="cursor-pointer">Ver fallidos (se pueden reenviar con “Reenviar a quienes no recibieron”)</summary>
          <ul className="mt-1 space-y-0.5">{job.fallidos_detalle.map((f) => <li key={f.email}>{f.email}: {f.error}</li>)}</ul>
        </details>
      )}
      {trabado && (
        <Button size="sm" variant="outline" onClick={reanudar} disabled={reanudando} data-testid="af-envio-reanudar-btn">
          {reanudando ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCw className="h-4 w-4 mr-1" />}Reanudar envío
        </Button>
      )}
    </div>
  );
};
